import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/apiService';

const CurrencyContext = createContext();

// Fallback currencies (used if DB fetch fails)
const fallbackCurrencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar', exchangeRate: 1, isDefault: true },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', exchangeRate: 3.67, isDefault: false },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', exchangeRate: 3.75, isDefault: false },
  { code: 'EUR', symbol: '€', name: 'Euro', exchangeRate: 0.92, isDefault: false },
  { code: 'GBP', symbol: '£', name: 'British Pound', exchangeRate: 0.79, isDefault: false },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', exchangeRate: 83.12, isDefault: false },
];

const normalizeCurrency = (c) => ({
  code: c.code,
  symbol: c.symbol,
  name: c.name,
  rate: c.exchangeRate ?? c.rate ?? 1,
  isDefault: c.isDefault || false
});

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState(fallbackCurrencies.map(normalizeCurrency));
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const saved = localStorage.getItem('selectedCurrency');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* fall through */ }
    }
    return normalizeCurrency(fallbackCurrencies[0]);
  });

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await api.get('/settings/currencies/public');
      const dbCurrencies = response.data;
      if (dbCurrencies && dbCurrencies.length > 0) {
        const normalized = dbCurrencies.map(normalizeCurrency);
        setCurrencies(normalized);

        const dbDefault = normalized.find(c => c.isDefault);
        const savedCode = localStorage.getItem('selectedCurrency');
        let savedCurrency = null;
        if (savedCode) {
          try { savedCurrency = JSON.parse(savedCode); } catch { /* ignore */ }
        }
        const userExplicitlyChosen = localStorage.getItem('currencyUserOverride') === 'true';
        const currentExists = savedCurrency ? normalized.find(c => c.code === savedCurrency.code) : null;

        if (!userExplicitlyChosen && dbDefault) {
          // User never explicitly chose — always follow admin default
          setSelectedCurrency(dbDefault);
          localStorage.setItem('selectedCurrency', JSON.stringify(dbDefault));
        } else if (!currentExists && dbDefault) {
          // User's chosen currency no longer exists — fall back to default
          setSelectedCurrency(dbDefault);
          localStorage.setItem('selectedCurrency', JSON.stringify(dbDefault));
          localStorage.removeItem('currencyUserOverride');
        } else if (currentExists) {
          // Update rate/symbol in case admin changed them
          setSelectedCurrency(currentExists);
          localStorage.setItem('selectedCurrency', JSON.stringify(currentExists));
        }
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCurrencies(); }, []);

  // Save to localStorage whenever selection changes
  useEffect(() => {
    localStorage.setItem('selectedCurrency', JSON.stringify(selectedCurrency));
  }, [selectedCurrency]);

  // Convert price from base currency (USD) to selected currency
  const convertPrice = useCallback((priceInBase) => {
    if (!priceInBase || isNaN(priceInBase)) return 0;
    return (parseFloat(priceInBase) * (selectedCurrency.rate || 1)).toFixed(2);
  }, [selectedCurrency.rate]);

  // Format price with currency symbol
  const formatPrice = useCallback((priceInBase) => {
    const converted = parseFloat(priceInBase) * (selectedCurrency.rate || 1);
    if (!priceInBase || isNaN(priceInBase)) return `${selectedCurrency.symbol}0`;
    return `${selectedCurrency.symbol}${parseFloat(converted.toFixed(2)).toLocaleString()}`;
  }, [selectedCurrency.symbol, selectedCurrency.rate]);

  // Format price range with currency symbol and code
  const formatPriceRange = useCallback((minPrice, maxPrice) => {
    if (!minPrice && !maxPrice) return `${selectedCurrency.symbol}0`;
    const rate = selectedCurrency.rate || 1;
    const minConverted = parseFloat((parseFloat(minPrice || 0) * rate).toFixed(2)).toLocaleString();
    const maxConverted = parseFloat((parseFloat(maxPrice || 0) * rate).toFixed(2)).toLocaleString();
    return `${selectedCurrency.symbol}${minConverted} - ${selectedCurrency.symbol}${maxConverted} ${selectedCurrency.code}`;
  }, [selectedCurrency.symbol, selectedCurrency.rate, selectedCurrency.code]);

  const changeCurrency = useCallback((currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currency);
      localStorage.setItem('currencyUserOverride', 'true');
    }
  }, [currencies]);

  // Force refresh (called after admin changes default)
  const refreshCurrencies = useCallback(async () => {
    await fetchCurrencies();
  }, [fetchCurrencies]);

  // Switch to admin-set default (clears user override)
  const resetToDefault = useCallback(() => {
    const def = currencies.find(c => c.isDefault);
    if (def) {
      setSelectedCurrency(def);
      localStorage.setItem('selectedCurrency', JSON.stringify(def));
      localStorage.removeItem('currencyUserOverride');
    }
  }, [currencies]);

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      currencies,
      changeCurrency,
      convertPrice,
      formatPrice,
      formatPriceRange,
      refreshCurrencies,
      resetToDefault
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
